import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Clock3,
  BadgeCheck,
  MapPin,
} from "lucide-react";

import cleaning2 from "../../assets/cleaning2.jpg";
import cleaning from "../../assets/cleaning.jpg";
import nurse2 from "../../assets/nurse2.jpg";

const images = [cleaning2, cleaning, nurse2];

const trustItems = [
  { icon: ShieldCheck, label: "Verified Staff" },
  { icon: BadgeCheck, label: "Background Checked" },
  { icon: Clock3, label: "Quick Response" },
];

const Hero = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[92vh] md:min-h-screen overflow-hidden">
      {/* Background slideshow */}
      <div className="absolute inset-0">
        {images.map((img, i) => (
          <motion.img
            key={i}
            src={img}
            alt="A2Z home services"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-[72%_center] md:object-center"
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{
              opacity: i === index ? 1 : 0,
              scale: i === index ? 1 : 1.02,
            }}
            transition={{ duration: 1.6 }}
          />
        ))}
      </div>

      {/* Premium overlay stack */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.78)_0%,rgba(255,255,255,0.58)_28%,rgba(255,255,255,0.26)_65%,rgba(255,255,255,0.18)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.88)_0%,rgba(255,255,255,0.62)_34%,rgba(255,255,255,0.14)_68%,rgba(255,255,255,0.04)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.16),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.12),transparent_24%),radial-gradient(circle_at_70%_20%,rgba(56,189,248,0.08),transparent_22%)]" />

      {/* Decorative glows */}
      <div className="absolute -top-20 -left-20 w-[280px] h-[280px] rounded-full bg-indigo-200/35 blur-3xl" />
      <div className="absolute top-[14%] right-[-70px] w-[280px] h-[280px] rounded-full bg-purple-200/30 blur-3xl" />
      <div className="absolute bottom-[-90px] left-[10%] w-[220px] h-[220px] rounded-full bg-sky-200/25 blur-3xl" />

      {/* Fine texture */}
      <div className="absolute inset-0 opacity-[0.04] [background-image:radial-gradient(#0f172a_0.8px,transparent_0.8px)] [background-size:18px_18px]" />

      {/* Premium frame */}
      <div className="absolute inset-4 md:inset-6 rounded-[28px] border border-white/30 pointer-events-none hidden md:block" />

      {/* Top curve */}
      <svg
        className="absolute top-0 left-0 w-full h-[140px] md:h-[190px] opacity-50"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="rgba(255,255,255,0.58)"
          d="M0,128L60,117.3C120,107,240,85,360,85.3C480,85,600,107,720,117.3C840,128,960,128,1080,112C1200,96,1320,64,1380,48L1440,32L1440,0L0,0Z"
        />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4 pt-28 pb-16 md:pt-36 md:pb-24 min-h-[92vh] md:min-h-screen flex items-center">
        <motion.div
          initial={{ opacity: 0, y: 38 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, ease: "easeOut" }}
          className="w-full max-w-5xl"
        >
          {/* Small label */}
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white/82 backdrop-blur px-4 py-2 text-sm text-indigo-700 font-medium mb-6 shadow-sm">
            Trusted Home Support & Job Opportunities
          </span>

          {/* Main heading */}
          <h1 className="text-[2.9rem] sm:text-5xl md:text-6xl lg:text-[4.6rem] font-bold leading-[0.95] tracking-[-0.04em] text-slate-900 mb-5 max-w-5xl">
            Trusted Home Help,
            <br />
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-sky-600 bg-clip-text text-transparent">
              Thoughtfully Connected
            </span>
          </h1>

          {/* Supporting text */}
          <p className="text-base sm:text-lg md:text-xl text-slate-700 max-w-3xl mb-4 leading-relaxed">
            A2Z Consultancy helps families hire trusted support and helps workers
            connect with genuine opportunities through a simple, guided experience.
          </p>

          {/* Location */}
          <div className="inline-flex items-center gap-2 text-sm md:text-base text-slate-600 mb-10">
            <MapPin className="w-4 h-4 text-indigo-600" />
            Mumbai, Navi Mumbai, Thane District, Nasik and Pune
          </div>

          {/* CTA row */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-start mb-10">
            <a href="/hire" className="w-full sm:w-auto">
              <button className="w-full sm:min-w-[220px] px-7 py-4 rounded-2xl bg-indigo-600 text-white text-base md:text-lg font-semibold shadow-[0_18px_40px_rgba(79,70,229,0.24)] hover:shadow-[0_22px_46px_rgba(79,70,229,0.30)] hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2">
                Hire Help
                <ArrowRight className="w-5 h-5" />
              </button>
            </a>

            <a href="/jobs" className="w-full sm:w-auto">
              <button className="w-full sm:min-w-[220px] px-7 py-4 rounded-2xl bg-white/80 backdrop-blur border border-white/80 text-slate-900 text-base md:text-lg font-semibold shadow-md hover:bg-white hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2">
                Find a Job
                <ArrowRight className="w-5 h-5 text-indigo-600" />
              </button>
            </a>
          </div>

          {/* Elegant trust row */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm md:text-base text-slate-700">
            {trustItems.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="inline-flex items-center gap-2">
                  <Icon className="w-4 h-4 text-indigo-600" />
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white/72 to-transparent" />
    </section>
  );
};

export default Hero;